import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  planApi, formatMoney, formatDate, formatDateTime, toMinor, INTERVAL_LABELS, STATUS_LABELS, formatBytes,
} from './planApi';
import { Badge, Modal, ConfirmModal, Field } from './PlanUi';

const PAGE_SIZE = 20;

const EVENT_LABELS = {
  created: 'Subscription created', status_changed: 'Status changed', plan_changed: 'Plan changed',
  plan_change_scheduled: 'Plan change scheduled', plan_change_cancelled: 'Scheduled change cancelled',
  cancel_scheduled: 'Cancellation scheduled', cancel_unscheduled: 'Cancellation withdrawn',
  period_extended: 'Renewed / extended', override_added: 'Override added', override_removed: 'Override removed',
  notes_updated: 'Notes updated',
};

const useDebounced = (value, delay = 350) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return v;
};

// Loads sellable (published) versions for plan pickers
const usePublishedPlans = () => {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    planApi.listPlans().then(res => setPlans(res.plans.filter(p => p.status === 'active' && p.current))).catch(() => setPlans([]));
  }, []);
  return plans;
};

// Plan + interval + price picker shared by assign and change-plan forms
const PlanPicker = ({ plans, value, onChange }) => {
  const [versions, setVersions] = useState([]);
  const plan = plans.find(p => p.planId === value.planId);

  useEffect(() => {
    if (!value.planId) { setVersions([]); return; }
    planApi.getPlan(value.planId)
      .then(res => setVersions(res.versions.filter(v => v.status === 'published')))
      .catch(() => setVersions([]));
  }, [value.planId]);

  const version = versions.find(v => v.version === Number(value.planVersion)) || versions.find(v => v.version === plan?.currentPublishedVersion);
  const options = (version?.pricing?.billingOptions || []).filter(o => o.isActive !== false);
  const listPrice = options.find(o => o.interval === value.interval)?.amountMinor;

  return (
    <>
      <div className="pm-grid pm-grid--2">
        <Field label="Plan">
          <select className="pm-input" value={value.planId} onChange={e => onChange({ ...value, planId: e.target.value, planVersion: '', interval: '' })}>
            <option value="">Select plan…</option>
            {plans.map(p => <option key={p.planId} value={p.planId}>{p.displayName}</option>)}
          </select>
        </Field>
        <Field label="Version" hint="Defaults to the version on sale">
          <select className="pm-input" value={value.planVersion} onChange={e => onChange({ ...value, planVersion: e.target.value, interval: '' })} disabled={!versions.length}>
            <option value="">{plan ? `v${plan.currentPublishedVersion} (on sale)` : '—'}</option>
            {versions.filter(v => v.version !== plan?.currentPublishedVersion).map(v => <option key={v.version} value={v.version}>v{v.version}</option>)}
          </select>
        </Field>
        <Field label="Billing interval">
          <select className="pm-input" value={value.interval} onChange={e => onChange({ ...value, interval: e.target.value })} disabled={!version}>
            <option value="">Select…</option>
            {options.map(o => <option key={o.interval} value={o.interval}>{INTERVAL_LABELS[o.interval]} — {formatMoney(o.amountMinor, version.pricing.currency)}</option>)}
            {version?.identity?.category === 'custom' && ['month', 'quarter', 'half_year', 'year'].filter(i => !options.some(o => o.interval === i)).map(i => (
              <option key={i} value={i}>{INTERVAL_LABELS[i]} — contract price</option>
            ))}
          </select>
        </Field>
        <Field label="Custom price (₹)" hint={listPrice !== undefined ? `Leave empty to use list price ${formatMoney(listPrice)}` : 'Required for contract pricing'}>
          <input type="number" min="0" step="0.01" className="pm-input" value={value.amount} onChange={e => onChange({ ...value, amount: e.target.value })} placeholder="—" />
        </Field>
        <Field label="Discount (₹ per period)">
          <input type="number" min="0" step="0.01" className="pm-input" value={value.discount} onChange={e => onChange({ ...value, discount: e.target.value })} placeholder="0" />
        </Field>
      </div>
      {version && (
        <p className="pm-muted pm-small">
          Trial: {version.pricing?.trialDays || 0} days · Grace: {version.pricing?.gracePeriodDays ?? 3} days · GST {version.pricing?.tax?.gstRatePercent ?? 18}% {version.pricing?.tax?.mode}
        </p>
      )}
    </>
  );
};

const pickerToBody = (v) => ({
  planId: v.planId,
  planVersion: v.planVersion ? Number(v.planVersion) : undefined,
  interval: v.interval,
  amountMinor: v.amount === '' ? undefined : toMinor(v.amount),
  discountMinor: v.discount === '' ? 0 : toMinor(v.discount),
});

// ─── Assign plan modal ───────────────────────────────────────────────────────
const AssignModal = ({ plans, onClose, onDone, notify, presetOwner }) => {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query);
  const [owners, setOwners] = useState([]);
  const [owner, setOwner] = useState(presetOwner || null);
  const [pick, setPick] = useState({ planId: '', planVersion: '', interval: '', amount: '', discount: '' });
  const [startAs, setStartAs] = useState('active');
  const [trialDays, setTrialDays] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (owner || debounced.trim().length < 2) { setOwners([]); return; }
    planApi.searchOwners(debounced.trim()).then(res => setOwners(res.owners)).catch(() => setOwners([]));
  }, [debounced, owner]);

  const ownerBlocked = owner?.subscription && !['cancelled', 'expired'].includes(owner.subscription.status);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await planApi.assign({
        ownerId: owner._id,
        ...pickerToBody(pick),
        startAs,
        trialDays: startAs === 'trialing' && trialDays !== '' ? Number(trialDays) : undefined,
        notes,
      });
      notify(`${pick.planId} assigned to ${owner.email}`);
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Assign plan"
      subtitle="Manually subscribe a restaurant owner (offline / manual billing)"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="pm-btn pm-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="pm-btn pm-btn--primary" onClick={submit} disabled={busy || !owner || ownerBlocked || !pick.planId || !pick.interval}>
            {busy ? 'Assigning…' : 'Assign plan'}
          </button>
        </>
      }
    >
      <Field label="Restaurant owner">
        {owner ? (
          <div className="pm-owner-chip">
            <div>
              <strong>{owner.name}</strong>
              <span className="pm-muted pm-small"> {owner.email}{owner.phone ? ` · ${owner.phone}` : ''}</span>
            </div>
            {!presetOwner && <button className="pm-link" onClick={() => setOwner(null)}>Change</button>}
          </div>
        ) : (
          <div className="pm-search">
            <input className="pm-input" autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by email, name, phone or user ID" />
            {owners.length > 0 && (
              <div className="pm-search__results">
                {owners.map(o => (
                  <button key={o._id} className="pm-search__item" onClick={() => setOwner(o)}>
                    <span><strong>{o.name}</strong> <span className="pm-muted">{o.email}</span></span>
                    {o.subscription ? <Badge status={o.subscription.status}>{o.subscription.planName} · {STATUS_LABELS[o.subscription.status]}</Badge> : <span className="pm-muted pm-small">No plan</span>}
                  </button>
                ))}
              </div>
            )}
            {debounced.trim().length >= 2 && owners.length === 0 && <p className="pm-muted pm-small">No restaurant owners found.</p>}
          </div>
        )}
      </Field>

      {ownerBlocked && (
        <div className="pm-callout pm-callout--warn">
          This owner already has a live subscription ({owner.subscription.planName}, {STATUS_LABELS[owner.subscription.status]}). Open it from the list and use <strong>Change plan</strong>.
        </div>
      )}

      <PlanPicker plans={plans} value={pick} onChange={setPick} />

      <div className="pm-grid pm-grid--2">
        <Field label="Start as">
          <select className="pm-input" value={startAs} onChange={e => setStartAs(e.target.value)}>
            <option value="active">Active (paid)</option>
            <option value="trialing">Free trial</option>
            <option value="pending_payment">Pending payment</option>
          </select>
        </Field>
        {startAs === 'trialing' && (
          <Field label="Trial days" hint="Leave empty to use the plan's trial length">
            <input type="number" min="1" max="365" className="pm-input" value={trialDays} onChange={e => setTrialDays(e.target.value)} />
          </Field>
        )}
      </div>
      <Field label="Internal notes">
        <textarea className="pm-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} maxLength={1000} placeholder="e.g. Paid via UPI, ref #…" />
      </Field>
      {error && <div className="pm-callout pm-callout--error">{error}</div>}
    </Modal>
  );
};

// ─── Subscription detail drawer ──────────────────────────────────────────────
const OverrideForm = ({ registry, onSubmit, busy }) => {
  const [kind, setKind] = useState('limit');
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [unlimited, setUnlimited] = useState(false);
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const defs = { feature: registry.features, limit: registry.limits, quota: registry.quotas, policy: registry.policies }[kind];
  const def = defs.find(d => d.key === key);
  const isBytes = def?.unit === 'bytes';

  const submit = (e) => {
    e.preventDefault();
    const body = { kind, key, reason, expiresAt: expiresAt || undefined };
    if (kind === 'feature') body.enabled = enabled;
    if (kind === 'limit' || kind === 'quota') {
      body.unlimited = unlimited;
      body.value = isBytes ? Math.round(Number(value) * 1024 * 1024) : Number(value);
    }
    if (kind === 'policy') {
      body.value = def.type === 'boolean' ? enabled : def.type === 'number' ? (value === '' ? null : Number(value)) : value;
    }
    onSubmit(body, () => { setKey(''); setValue(''); setReason(''); setExpiresAt(''); setUnlimited(false); });
  };

  return (
    <form className="pm-override-form" onSubmit={submit}>
      <div className="pm-grid pm-grid--2">
        <Field label="Type">
          <select className="pm-input" value={kind} onChange={e => { setKind(e.target.value); setKey(''); setValue(''); }}>
            <option value="limit">Limit</option>
            <option value="quota">Quota</option>
            <option value="feature">Feature</option>
            <option value="policy">Policy</option>
          </select>
        </Field>
        <Field label="Entitlement">
          <select className="pm-input" value={key} onChange={e => { setKey(e.target.value); setValue(''); }}>
            <option value="">Select…</option>
            {defs.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
        </Field>
        {def && (kind === 'feature' || (kind === 'policy' && def.type === 'boolean')) && (
          <Field label="Value">
            <select className="pm-input" value={String(enabled)} onChange={e => setEnabled(e.target.value === 'true')}>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </Field>
        )}
        {def && (kind === 'limit' || kind === 'quota') && (
          <Field label={`Value${isBytes ? ' (MB)' : ''}`}>
            <div className="pm-inline-form">
              <input type="number" min="0" className="pm-input" value={value} disabled={unlimited} onChange={e => setValue(e.target.value)} />
              <label className="pm-check"><input type="checkbox" checked={unlimited} onChange={e => setUnlimited(e.target.checked)} /> Unlimited</label>
            </div>
          </Field>
        )}
        {def && kind === 'policy' && def.type === 'enum' && (
          <Field label="Value">
            <select className="pm-input" value={value} onChange={e => setValue(e.target.value)}>
              <option value="">Select…</option>
              {def.options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
            </select>
          </Field>
        )}
        {def && kind === 'policy' && def.type === 'number' && (
          <Field label="Value"><input type="number" min="0" className="pm-input" value={value} onChange={e => setValue(e.target.value)} /></Field>
        )}
        <Field label="Expires (optional)">
          <input type="date" className="pm-input" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
        </Field>
      </div>
      <Field label="Reason (required, audited)">
        <input className="pm-input" value={reason} onChange={e => setReason(e.target.value)} maxLength={500} placeholder="e.g. Enterprise contract #123 — 10 outlets" />
      </Field>
      <button type="submit" className="pm-btn pm-btn--primary pm-btn--sm" disabled={busy || !key || !reason.trim()}>Add override</button>
    </form>
  );
};

const entValue = (def, ent) => {
  if (!ent) return '—';
  if (ent.unlimited) return 'Unlimited';
  return def.unit === 'bytes' ? formatBytes(ent.value) : ent.value;
};

const SubscriptionDrawer = ({ id, registry, plans, onClose, onChanged, notify }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null);   // 'status' | 'renew' | 'change' | 'cancelEnd' | 'override'
  const [statusForm, setStatusForm] = useState({ toStatus: '', reason: '' });
  const [renewPeriods, setRenewPeriods] = useState(1);
  const [renewNote, setRenewNote] = useState('');
  const [change, setChange] = useState({ planId: '', planVersion: '', interval: '', amount: '', discount: '', effective: 'immediate', reason: '' });
  const [notes, setNotes] = useState('');
  const [tab, setTab] = useState('overview');

  const load = useCallback(async () => {
    try {
      const res = await planApi.getSubscription(id);
      setData(res);
      setNotes(res.subscription.notes || '');
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      notify(successMsg);
      setAction(null);
      // Plan changes create a new subscription record — follow it
      const newId = res?.subscription?._id;
      if (newId && newId !== id) onChanged(newId); else { await load(); onChanged(); }
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Modal title="Subscription" onClose={onClose}><div className="pm-callout pm-callout--error">{error}</div></Modal>;
  if (!data) return <Modal title="Subscription" onClose={onClose}><div className="pm-empty"><span className="pm-spinner" /> Loading…</div></Modal>;

  const { subscription: s, events, overrides, usage, resolved, allowedTransitions, history } = data;
  const terminal = ['cancelled', 'expired'].includes(s.status);
  const live = s.isCurrent && !terminal;
  const net = s.billing.amountMinor - (s.billing.discountMinor || 0);

  return (
    <div className="pm-drawer-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <aside className="pm-drawer">
        <div className="pm-drawer__head">
          <div>
            <h3>{s.owner?.name || 'Owner'}</h3>
            <p className="pm-muted pm-small">{s.owner?.email}{s.owner?.phone ? ` · ${s.owner.phone}` : ''}</p>
          </div>
          <button className="pm-icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="pm-drawer__summary">
          <div>
            <span className="pm-muted pm-small">Plan</span>
            <strong>{s.planName} <span className="pm-muted">v{s.planVersion}</span></strong>
          </div>
          <div>
            <span className="pm-muted pm-small">Status</span>
            <span><Badge status={s.status} />{!s.isCurrent && <Badge tone="muted">Historical</Badge>}</span>
          </div>
          <div>
            <span className="pm-muted pm-small">Billing</span>
            <strong>{formatMoney(net, s.billing.currency)} / {INTERVAL_LABELS[s.billing.interval]?.toLowerCase()}</strong>
            {s.billing.discountMinor > 0 && <span className="pm-muted pm-small">list {formatMoney(s.billing.amountMinor)} − {formatMoney(s.billing.discountMinor)}</span>}
          </div>
          <div>
            <span className="pm-muted pm-small">{s.status === 'trialing' ? 'Trial ends' : 'Current period'}</span>
            <strong>{s.status === 'trialing' ? formatDate(s.period.trialEndsAt) : `${formatDate(s.period.currentPeriodStart)} → ${formatDate(s.period.currentPeriodEnd)}`}</strong>
          </div>
        </div>

        {s.period.cancelAtPeriodEnd && live && <div className="pm-callout pm-callout--warn">Cancels on {formatDate(s.period.currentPeriodEnd)}.</div>}
        {s.status === 'grace_period' && <div className="pm-callout pm-callout--warn">In grace period until {formatDateTime(s.period.graceEndsAt)}.</div>}
        {s.scheduledChange?.planId && live && (
          <div className="pm-callout">
            Scheduled change to <strong>{s.scheduledChange.planId} v{s.scheduledChange.planVersion}</strong> ({INTERVAL_LABELS[s.scheduledChange.interval]}) on {formatDate(s.scheduledChange.effectiveAt)}.
            <button className="pm-link" disabled={busy} onClick={() => run(() => planApi.cancelScheduled(s._id), 'Scheduled change cancelled')}> Cancel change</button>
          </div>
        )}
        {!resolved.hasAccess && s.isCurrent && <div className="pm-callout pm-callout--error">Owner currently has no plan access.</div>}

        {live && (
          <div className="pm-drawer__actions">
            <button className="pm-btn pm-btn--primary pm-btn--sm" onClick={() => setAction('renew')}>Record renewal</button>
            <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => { setChange(c => ({ ...c, planId: '', planVersion: '', interval: s.billing.interval })); setAction('change'); }}>Change plan</button>
            <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => { setStatusForm({ toStatus: allowedTransitions[0] || '', reason: '' }); setAction('status'); }} disabled={!allowedTransitions.length}>Change status</button>
            <button className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => setAction('cancelEnd')}>
              {s.period.cancelAtPeriodEnd ? 'Keep subscription' : 'Cancel at period end'}
            </button>
          </div>
        )}

        <div className="pm-tabs pm-tabs--sm">
          {['overview', 'entitlements', 'overrides', 'activity'].map(t => (
            <button key={t} className={`pm-tab ${tab === t ? 'pm-tab--active' : ''}`} onClick={() => setTab(t)}>
              {t[0].toUpperCase() + t.slice(1)}{t === 'overrides' && overrides.length ? ` (${overrides.length})` : ''}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="pm-drawer__body">
            <h4>Usage this period</h4>
            {usage.length === 0 ? <p className="pm-muted pm-small">No usage tracked.</p> : (
              <div className="pm-usage">
                {usage.map(u => {
                  const def = registry.quotas.find(q => q.key === u.key);
                  const cap = u.limit?.unlimited ? null : u.limit?.value;
                  const pct = cap ? Math.min(100, Math.round((u.used / cap) * 100)) : 0;
                  return (
                    <div key={u.key} className="pm-usage__row">
                      <div className="pm-usage__label">
                        <span>{def?.label || u.key}</span>
                        <span className="pm-mono pm-small">{def?.unit === 'bytes' ? formatBytes(u.used) : u.used} / {cap === null ? '∞' : def?.unit === 'bytes' ? formatBytes(cap) : cap}</span>
                      </div>
                      <div className="pm-meter"><div className={`pm-meter__fill ${pct >= 90 ? 'pm-meter__fill--bad' : pct >= 75 ? 'pm-meter__fill--warn' : ''}`} style={{ width: `${cap === null ? 0 : pct}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            )}
            <h4>Notes</h4>
            <textarea className="pm-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} maxLength={1000} />
            {notes !== (s.notes || '') && (
              <button className="pm-btn pm-btn--ghost pm-btn--sm" disabled={busy} onClick={() => run(() => planApi.updateNotes(s._id, notes), 'Notes saved')}>Save notes</button>
            )}
            <h4>Subscription history</h4>
            <div className="pm-history">
              {history.map(h => (
                <div key={h._id} className={`pm-history__row ${h._id === s._id ? 'pm-history__row--current' : ''}`}>
                  <span>{h.planName || h.planId} v{h.planVersion}</span>
                  <Badge status={h.status} />
                  <span className="pm-muted pm-small">{formatDate(h.period?.startedAt)} → {h.period?.endedAt ? formatDate(h.period.endedAt) : 'now'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'entitlements' && (
          <div className="pm-drawer__body">
            <p className="pm-muted pm-small">Effective entitlements = plan version + active overrides.{resolved.readOnly ? ' Currently read-only (grace period policy).' : ''}</p>
            <h4>Features</h4>
            <div className="pm-chip-list">
              {registry.features.map(d => (
                <span key={d.key} className={`pm-chip ${resolved.features[d.key] ? 'pm-chip--on' : 'pm-chip--off'}`}>{resolved.features[d.key] ? '✓' : '✕'} {d.label}</span>
              ))}
            </div>
            <h4>Limits</h4>
            <table className="pm-kv">
              <tbody>
                {registry.limits.map(d => (
                  <tr key={d.key}><td>{d.label} <span className="pm-muted pm-small">/ {d.scope}</span></td><td className="pm-mono">{entValue(d, resolved.limits[d.key])}</td></tr>
                ))}
              </tbody>
            </table>
            <h4>Quotas</h4>
            <table className="pm-kv">
              <tbody>
                {registry.quotas.map(d => (
                  <tr key={d.key}><td>{d.label}</td><td className="pm-mono">{entValue(d, resolved.quotas[d.key])}</td></tr>
                ))}
              </tbody>
            </table>
            <h4>Policies</h4>
            <table className="pm-kv">
              <tbody>
                {registry.policies.map(d => (
                  <tr key={d.key}><td>{d.label}</td><td className="pm-mono">{String(resolved.policies[d.key] ?? '—')}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'overrides' && (
          <div className="pm-drawer__body">
            <p className="pm-muted pm-small">Overrides apply to this owner on top of any plan — use for negotiated Enterprise limits or temporary extensions.</p>
            {overrides.length === 0 && <p className="pm-muted">No active overrides.</p>}
            {overrides.map(o => {
              const def = { feature: registry.features, limit: registry.limits, quota: registry.quotas, policy: registry.policies }[o.kind]?.find(d => d.key === o.key);
              const val = o.kind === 'feature' ? (o.enabled ? 'Enabled' : 'Disabled') : o.unlimited ? 'Unlimited' : def?.unit === 'bytes' ? formatBytes(o.value) : String(o.value);
              return (
                <div key={o._id} className={`pm-override ${o.expired ? 'pm-override--expired' : ''}`}>
                  <div>
                    <strong>{def?.label || o.key}</strong> <span className="pm-muted pm-small">({o.kind})</span> → <span className="pm-mono">{val}</span>
                    <div className="pm-muted pm-small">{o.reason} · {o.expiresAt ? `${o.expired ? 'expired' : 'expires'} ${formatDate(o.expiresAt)}` : 'no expiry'}</div>
                  </div>
                  <button className="pm-link pm-link--danger" disabled={busy} onClick={() => run(() => planApi.removeOverride(s._id, o._id), 'Override removed')}>Remove</button>
                </div>
              );
            })}
            {s.isCurrent && (
              <>
                <h4>Add override</h4>
                <OverrideForm registry={registry} busy={busy}
                  onSubmit={(body, reset) => run(async () => { await planApi.addOverride(s._id, body); reset(); }, 'Override added')} />
              </>
            )}
          </div>
        )}

        {tab === 'activity' && (
          <div className="pm-drawer__body">
            <ol className="pm-timeline">
              {events.map(e => (
                <li key={e._id}>
                  <div className="pm-timeline__title">
                    {EVENT_LABELS[e.type] || e.type}
                    {e.fromStatus || e.toStatus ? <span className="pm-muted"> {e.fromStatus ? `${STATUS_LABELS[e.fromStatus]} → ` : ''}{STATUS_LABELS[e.toStatus] || ''}</span> : null}
                  </div>
                  {e.data?.to && <div className="pm-small">{e.data.from?.planId} v{e.data.from?.planVersion} → {e.data.to.planId} v{e.data.to.planVersion}</div>}
                  {e.data?.key && <div className="pm-small pm-mono">{e.data.key}</div>}
                  {e.reason && <div className="pm-small">“{e.reason}”</div>}
                  <div className="pm-muted pm-small">{formatDateTime(e.createdAt)} · {e.actorType}</div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {action === 'renew' && (
          <ConfirmModal title="Record renewal" confirmLabel="Renew" busy={busy} onClose={() => setAction(null)}
            onConfirm={() => run(() => planApi.renew(s._id, { periods: Number(renewPeriods), reason: renewNote }), 'Subscription renewed')}
            message={`Extends the subscription by the selected number of ${INTERVAL_LABELS[s.billing.interval]?.toLowerCase()} periods and sets it Active. Use after receiving payment.`}>
            <div className="pm-grid pm-grid--2">
              <Field label="Periods"><input type="number" min="1" max="24" className="pm-input" value={renewPeriods} onChange={e => setRenewPeriods(e.target.value)} /></Field>
              <Field label="Payment reference / note"><input className="pm-input" value={renewNote} onChange={e => setRenewNote(e.target.value)} /></Field>
            </div>
          </ConfirmModal>
        )}

        {action === 'status' && (
          <ConfirmModal title="Change status" confirmLabel="Apply" danger={['suspended', 'cancelled', 'expired'].includes(statusForm.toStatus)} busy={busy}
            onClose={() => setAction(null)}
            onConfirm={() => run(() => planApi.changeStatus(s._id, { ...statusForm, expectedStatus: s.status }), 'Status updated')}>
            <Field label="New status">
              <select className="pm-input" value={statusForm.toStatus} onChange={e => setStatusForm(f => ({ ...f, toStatus: e.target.value }))}>
                {allowedTransitions.map(t => <option key={t} value={t}>{STATUS_LABELS[t]}</option>)}
              </select>
            </Field>
            <Field label="Reason (required, audited)">
              <input className="pm-input" value={statusForm.reason} onChange={e => setStatusForm(f => ({ ...f, reason: e.target.value }))} />
            </Field>
            {['cancelled', 'expired'].includes(statusForm.toStatus) && <p className="pm-field__error">This ends the subscription immediately and cannot be undone.</p>}
          </ConfirmModal>
        )}

        {action === 'cancelEnd' && (
          <ConfirmModal
            title={s.period.cancelAtPeriodEnd ? 'Keep subscription?' : 'Cancel at period end?'}
            confirmLabel={s.period.cancelAtPeriodEnd ? 'Keep subscription' : 'Schedule cancellation'}
            danger={!s.period.cancelAtPeriodEnd} busy={busy} onClose={() => setAction(null)}
            onConfirm={() => run(() => planApi.cancelAtPeriodEnd(s._id, { cancel: !s.period.cancelAtPeriodEnd }), s.period.cancelAtPeriodEnd ? 'Cancellation withdrawn' : 'Cancellation scheduled')}
            message={s.period.cancelAtPeriodEnd
              ? 'The scheduled cancellation will be removed.'
              : `The owner keeps access until ${formatDate(s.period.currentPeriodEnd)}, then the subscription is cancelled.`} />
        )}

        {action === 'change' && (
          <Modal title="Change plan" subtitle={`Currently ${s.planName} v${s.planVersion}`} wide onClose={() => setAction(null)}
            footer={
              <>
                <button className="pm-btn pm-btn--ghost" onClick={() => setAction(null)}>Cancel</button>
                <button className="pm-btn pm-btn--primary" disabled={busy || !change.planId || !change.interval}
                  onClick={() => run(() => planApi.changePlan(s._id, { ...pickerToBody(change), effective: change.effective, reason: change.reason }),
                    change.effective === 'immediate' ? 'Plan changed' : 'Plan change scheduled')}>
                  {busy ? 'Working…' : change.effective === 'immediate' ? 'Change now' : 'Schedule change'}
                </button>
              </>
            }>
            <PlanPicker plans={plans} value={change} onChange={v => setChange(c => ({ ...c, ...v }))} />
            <div className="pm-grid pm-grid--2">
              <Field label="When" hint={change.effective === 'immediate' ? 'Starts a new billing period today. Proration is not calculated — settle manually.' : `Applies on ${formatDate(s.period.currentPeriodEnd)} (period end)`}>
                <select className="pm-input" value={change.effective} onChange={e => setChange(c => ({ ...c, effective: e.target.value }))}>
                  <option value="immediate">Immediately (upgrade)</option>
                  <option value="period_end">At period end (downgrade)</option>
                </select>
              </Field>
              <Field label="Reason">
                <input className="pm-input" value={change.reason} onChange={e => setChange(c => ({ ...c, reason: e.target.value }))} />
              </Field>
            </div>
            <p className="pm-muted pm-small">Downgrades never delete data: owners above a new limit keep existing records but cannot create more.</p>
          </Modal>
        )}
      </aside>
    </div>
  );
};

// ─── Main list ───────────────────────────────────────────────────────────────
const SubscriptionManager = ({ registry, notify }) => {
  const plans = usePublishedPlans();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({ q: '', status: '', planId: '', history: false });
  const debouncedQ = useDebounced(filters.q);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const [maintBusy, setMaintBusy] = useState(false);
  const reqId = useRef(0);

  const loadStats = useCallback(() => {
    planApi.subscriptionStats().then(res => setStats(res.stats)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const my = ++reqId.current;
    setLoading(true);
    try {
      const res = await planApi.listSubscriptions({
        q: debouncedQ, status: filters.status, planId: filters.planId,
        history: filters.history ? 'true' : undefined, page, limit: PAGE_SIZE,
      });
      if (my !== reqId.current) return;   // ignore stale responses
      setItems(res.subscriptions);
      setPagination(res.pagination);
      setError('');
    } catch (err) {
      if (my === reqId.current) setError(err.message);
    } finally {
      if (my === reqId.current) setLoading(false);
    }
  }, [debouncedQ, filters.status, filters.planId, filters.history, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { setPage(1); }, [debouncedQ, filters.status, filters.planId, filters.history]);

  const refresh = () => { load(); loadStats(); };

  const runMaintenance = async () => {
    setMaintBusy(true);
    try {
      const res = await planApi.runMaintenance();
      const s = res.summary;
      const changed = s.cancelledAtPeriodEnd + s.scheduledChangesApplied + s.trialsEnded + s.movedToGrace + s.suspended;
      notify(changed ? `Lifecycle run: ${changed} subscription(s) updated${s.errors ? `, ${s.errors} error(s)` : ''}` : 'Lifecycle run: nothing due');
      refresh();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setMaintBusy(false);
    }
  };

  const planNames = useMemo(() => Object.fromEntries(plans.map(p => [p.planId, p.displayName])), [plans]);
  const atRisk = stats ? (stats.byStatus.past_due || 0) + (stats.byStatus.grace_period || 0) + (stats.byStatus.pending_payment || 0) : null;

  return (
    <div>
      <div className="pm-stats">
        <div className="pm-stat"><span className="pm-stat__label">MRR</span><span className="pm-stat__value">{stats ? formatMoney(stats.mrrMinor) : '—'}</span><span className="pm-stat__sub">net of discounts, pre-tax</span></div>
        <div className="pm-stat"><span className="pm-stat__label">Active</span><span className="pm-stat__value">{stats?.byStatus.active ?? '—'}</span><span className="pm-stat__sub">{stats?.byStatus.trialing ?? 0} trialing</span></div>
        <div className="pm-stat"><span className="pm-stat__label">Needs attention</span><span className="pm-stat__value pm-stat__value--warn">{atRisk ?? '—'}</span><span className="pm-stat__sub">pending · past due · grace</span></div>
        <div className="pm-stat"><span className="pm-stat__label">Suspended</span><span className="pm-stat__value pm-stat__value--bad">{stats?.byStatus.suspended ?? '—'}</span><span className="pm-stat__sub">no access</span></div>
        <div className="pm-stat"><span className="pm-stat__label">Owners without plan</span><span className="pm-stat__value">{stats?.ownersWithoutPlan ?? '—'}</span><span className="pm-stat__sub">of {stats?.totalOwners ?? '—'} owners</span></div>
      </div>

      <div className="pm-toolbar">
        <div className="pm-toolbar__filters">
          <input className="pm-input" value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))} placeholder="Search owner email, name, phone, ID" />
          <select className="pm-input pm-input--sm" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">All statuses</option>
            {registry.subscriptionStatuses.map(st => <option key={st} value={st}>{STATUS_LABELS[st]}</option>)}
          </select>
          <select className="pm-input pm-input--sm" value={filters.planId} onChange={e => setFilters(f => ({ ...f, planId: e.target.value }))}>
            <option value="">All plans</option>
            {plans.map(p => <option key={p.planId} value={p.planId}>{p.displayName}</option>)}
          </select>
          <label className="pm-check"><input type="checkbox" checked={filters.history} onChange={e => setFilters(f => ({ ...f, history: e.target.checked }))} /> Include history</label>
        </div>
        <div className="pm-toolbar__right">
          <button className="pm-btn pm-btn--ghost" onClick={runMaintenance} disabled={maintBusy} title="Apply due trial ends, lapsed renewals, grace expiry and scheduled changes">
            {maintBusy ? 'Running…' : 'Run lifecycle check'}
          </button>
          <button className="pm-btn pm-btn--primary" onClick={() => setShowAssign(true)} disabled={!plans.length} title={plans.length ? '' : 'Publish a plan first'}>+ Assign plan</button>
        </div>
      </div>

      {error && <div className="pm-callout pm-callout--error">{error}</div>}

      <div className="pm-table-wrap">
        <table className="pm-table">
          <thead>
            <tr><th>Owner</th><th>Plan</th><th>Status</th><th>Billing</th><th>Period ends</th><th>Updated</th></tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && <tr><td colSpan={6} className="pm-empty-cell"><span className="pm-spinner" /> Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="pm-empty-cell">No subscriptions match these filters.</td></tr>}
            {items.map(s => (
              <tr key={s._id} onClick={() => setOpenId(s._id)} className={loading ? 'pm-row--loading' : ''}>
                <td><strong>{s.owner?.name || '—'}</strong><div className="pm-muted pm-small">{s.owner?.email}</div></td>
                <td>{s.planName || planNames[s.planId] || s.planId} <span className="pm-muted">v{s.planVersion}</span>
                  {s.scheduledChange?.planId && <div className="pm-small pm-warn-text">→ {s.scheduledChange.planId} on {formatDate(s.scheduledChange.effectiveAt)}</div>}
                </td>
                <td>
                  <Badge status={s.status} />
                  {s.period?.cancelAtPeriodEnd && !['cancelled', 'expired'].includes(s.status) && <div className="pm-small pm-warn-text">cancels at end</div>}
                  {!s.isCurrent && <div className="pm-small pm-muted">historical</div>}
                </td>
                <td className="pm-mono">{formatMoney(s.billing.amountMinor - (s.billing.discountMinor || 0), s.billing.currency)}<div className="pm-muted pm-small">{INTERVAL_LABELS[s.billing.interval]}</div></td>
                <td>{formatDate(s.status === 'trialing' ? s.period.trialEndsAt : s.period.currentPeriodEnd)}</td>
                <td className="pm-muted pm-small">{formatDate(s.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="pm-pagination">
          <button className="pm-btn pm-btn--ghost pm-btn--sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span className="pm-muted pm-small">Page {pagination.page} of {pagination.pages} · {pagination.total} total</span>
          <button className="pm-btn pm-btn--ghost pm-btn--sm" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {showAssign && <AssignModal plans={plans} notify={notify} onClose={() => setShowAssign(false)} onDone={() => { setShowAssign(false); refresh(); }} />}
      {openId && (
        <SubscriptionDrawer
          key={openId}
          id={openId}
          registry={registry}
          plans={plans}
          notify={notify}
          onClose={() => setOpenId(null)}
          onChanged={(newId) => { refresh(); if (newId) setOpenId(newId); }}
        />
      )}
    </div>
  );
};

export default SubscriptionManager;
